import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { 
  Settings as SettingsIcon, Database, RefreshCw, AlertCircle, 
  CheckCircle, Cloud, FolderOpen, Link2, Unlink, Bell, FileText
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const SettingsPage = () => {
  const [searchParams] = useSearchParams();
  const [googleStatus, setGoogleStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncingData, setSyncingData] = useState(false);
  
  // Form states
  const [spreadsheetId, setSpreadsheetId] = useState("");
  const [driveFolderId, setDriveFolderId] = useState("");
  const [autoSync, setAutoSync] = useState(false);

  // Müşteri alacaklarını senkronize et
  const handleSyncCustomerReceivables = async () => {
    setSyncingData(true);
    try {
      const response = await axios.post(`${API}/sync-customer-receivables`, {}, { withCredentials: true });
      toast.success(
        `Senkronizasyon tamamlandı! Toplam alacak: ₺${response.data.total_receivable?.toLocaleString('tr-TR', {minimumFractionDigits: 2})}`,
        { duration: 5000 }
      );
      // Sayfayı yenile
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      console.error("Sync error:", error);
      toast.error(error.response?.data?.detail || "Senkronizasyon hatası");
    } finally {
      setSyncingData(false);
    }
  };

  // Excel işlemlerini import et
  const [importingTransactions, setImportingTransactions] = useState(false);
  
  const handleImportExcelTransactions = async () => {
    setImportingTransactions(true);
    try {
      const response = await axios.post(`${API}/import-excel-transactions`, {}, { withCredentials: true });
      toast.success(
        `${response.data.stats.transactions_added} işlem başarıyla import edildi!`,
        { duration: 5000 }
      );
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.response?.data?.detail || "Import hatası");
    } finally {
      setImportingTransactions(false);
    }
  };

  const fetchGoogleStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/google/status`, { withCredentials: true });
      setGoogleStatus(response.data);
      if (response.data.spreadsheet_id) setSpreadsheetId(response.data.spreadsheet_id);
      if (response.data.drive_folder_id) setDriveFolderId(response.data.drive_folder_id);
      setAutoSync(response.data.auto_sync || false);
    } catch (error) {
      // Silent error handling
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGoogleStatus();
    
    // Check URL params for connection status
    const googleConnected = searchParams.get("google_connected");
    const googleError = searchParams.get("google_error");
    
    if (googleConnected === "true") {
      toast.success("Google hesabı başarıyla bağlandı!");
    } else if (googleError) {
      toast.error(`Google bağlantı hatası: ${googleError}`);
    }
  }, [searchParams, fetchGoogleStatus]);

  const handleConnectGoogle = async () => {
    setConnecting(true);
    try {
      const response = await axios.get(`${API}/google/connect`, { withCredentials: true });
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      if (error.response?.data?.detail?.includes("not configured")) {
        toast.error("Google OAuth henüz yapılandırılmamış. Lütfen .env dosyasına GOOGLE_CLIENT_ID ve GOOGLE_CLIENT_SECRET ekleyin.");
      } else {
        toast.error("Bağlantı hatası oluştu");
      }
      setConnecting(false);
    }
  };

  const handleDisconnectGoogle = async () => {
    try {
      await axios.post(`${API}/google/disconnect`, {}, { withCredentials: true });
      setGoogleStatus({ connected: false });
      setSpreadsheetId("");
      setDriveFolderId("");
      setAutoSync(false);
      toast.success("Google hesabı bağlantısı kesildi");
    } catch (error) {
      toast.error("Bağlantı kesme hatası");
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.post(`${API}/google/settings`, {
        spreadsheet_id: spreadsheetId || null,
        drive_folder_id: driveFolderId || null,
        auto_sync: autoSync
      }, { withCredentials: true });
      toast.success("Ayarlar kaydedildi");
    } catch (error) {
      toast.error("Ayarlar kaydedilemedi");
    }
  };

  const handleSyncNow = async () => {
    if (!spreadsheetId) {
      toast.error("Lütfen önce Spreadsheet ID girin");
      return;
    }
    
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/google/sheets/sync`, {}, { withCredentials: true });
      toast.success(`Senkronizasyon tamamlandı: ${response.data.customers} müşteri, ${response.data.payments} ödeme, ${response.data.expenses} gider`);
      fetchGoogleStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Senkronizasyon hatası");
    } finally {
      setSyncing(false);
    }
  };

  const extractSpreadsheetId = (url) => {
    // Extract ID from Google Sheets URL
    const match = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    return match ? match[1] : url;
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-outfit font-semibold text-zinc-50">Ayarlar</h1>
        <p className="text-zinc-500 text-sm mt-1">Sistem ayarları ve entegrasyonlar</p>
      </div>

      {/* Google Integration */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-sm ${googleStatus?.connected ? 'bg-emerald-400/10' : 'bg-zinc-800'}`}>
            <Cloud className={`w-6 h-6 ${googleStatus?.connected ? 'text-emerald-400' : 'text-zinc-500'}`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-outfit font-medium text-zinc-50">Google Entegrasyonu</h3>
                <p className="text-sm text-zinc-500 mt-1">
                  Google Sheets ve Drive ile senkronizasyon
                </p>
              </div>
              {googleStatus?.connected ? (
                <span className="flex items-center gap-2 text-sm text-emerald-400">
                  <CheckCircle className="w-4 h-4" />
                  Bağlı
                </span>
              ) : (
                <span className="flex items-center gap-2 text-sm text-zinc-500">
                  <Unlink className="w-4 h-4" />
                  Bağlı Değil
                </span>
              )}
            </div>

            {!googleStatus?.connected ? (
              <div className="mt-4">
                <div className="p-4 bg-amber-400/10 border border-amber-400/20 rounded-sm mb-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-amber-400 font-medium">Google OAuth Gerekli</p>
                      <p className="text-xs text-amber-400/80 mt-1">
                        Google Cloud Console'dan OAuth credentials oluşturun ve .env dosyasına ekleyin:
                        GOOGLE_CLIENT_ID ve GOOGLE_CLIENT_SECRET
                      </p>
                    </div>
                  </div>
                </div>
                <Button 
                  onClick={handleConnectGoogle}
                  disabled={connecting}
                  className="bg-amber-400 hover:bg-amber-300 text-zinc-950"
                  data-testid="connect-google-btn"
                >
                  {connecting ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Bağlanıyor...
                    </>
                  ) : (
                    <>
                      <Link2 className="w-4 h-4 mr-2" />
                      Google ile Bağlan
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                {/* Spreadsheet Settings */}
                <div className="p-4 bg-zinc-950 border border-zinc-800 rounded-sm space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Database className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm font-medium text-zinc-50">Google Sheets</span>
                  </div>
                  
                  <div>
                    <Label className="text-zinc-400 text-xs">Spreadsheet ID veya URL</Label>
                    <Input 
                      value={spreadsheetId}
                      onChange={(e) => setSpreadsheetId(extractSpreadsheetId(e.target.value))}
                      placeholder="https://docs.google.com/spreadsheets/d/... veya ID"
                      className="bg-zinc-900 border-zinc-700 mt-1 text-sm"
                      data-testid="spreadsheet-id-input"
                    />
                    <p className="text-xs text-zinc-600 mt-1">
                      Sheets'te 3 sayfa oluşturun: "Müşteriler", "Ödemeler", "Giderler"
                    </p>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={autoSync}
                        onCheckedChange={setAutoSync}
                        data-testid="auto-sync-switch"
                      />
                      <Label className="text-zinc-400 text-sm">Otomatik Senkronizasyon</Label>
                    </div>
                    {googleStatus?.last_sync && (
                      <span className="text-xs text-zinc-500">
                        Son: {new Date(googleStatus.last_sync).toLocaleString('tr-TR')}
                      </span>
                    )}
                  </div>
                </div>

                {/* Drive Settings */}
                <div className="p-4 bg-zinc-950 border border-zinc-800 rounded-sm space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FolderOpen className="w-4 h-4 text-sky-400" />
                    <span className="text-sm font-medium text-zinc-50">Google Drive</span>
                  </div>
                  
                  <div>
                    <Label className="text-zinc-400 text-xs">Drive Klasör ID (opsiyonel)</Label>
                    <Input 
                      value={driveFolderId}
                      onChange={(e) => setDriveFolderId(e.target.value)}
                      placeholder="Yedekleme klasörü ID'si"
                      className="bg-zinc-900 border-zinc-700 mt-1 text-sm"
                      data-testid="drive-folder-input"
                    />
                    <p className="text-xs text-zinc-600 mt-1">
                      Boş bırakırsanız dosyalar Drive'ın kök dizinine yüklenir
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-3">
                  <Button 
                    onClick={handleSaveSettings}
                    className="bg-amber-400 hover:bg-amber-300 text-zinc-950"
                    data-testid="save-settings-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Kaydet
                  </Button>
                  <Button 
                    onClick={handleSyncNow}
                    disabled={syncing || !spreadsheetId}
                    variant="outline"
                    className="border-zinc-700 hover:bg-zinc-800"
                    data-testid="sync-now-btn"
                  >
                    {syncing ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Senkronize Ediliyor...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Şimdi Senkronize Et
                      </>
                    )}
                  </Button>
                  <Button 
                    onClick={handleDisconnectGoogle}
                    variant="outline"
                    className="border-rose-800 text-rose-400 hover:bg-rose-950"
                    data-testid="disconnect-google-btn"
                  >
                    <Unlink className="w-4 h-4 mr-2" />
                    Bağlantıyı Kes
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Data Sync - Müşteri Alacakları */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-emerald-400/10 rounded-sm">
            <Database className="w-6 h-6 text-emerald-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-outfit font-medium text-zinc-50">Veri Senkronizasyonu</h3>
            <p className="text-sm text-zinc-500 mt-1">
              Müşteri alacaklarını Excel verilerine göre senkronize et
            </p>

            <div className="mt-4 p-4 bg-amber-400/10 border border-amber-400/20 rounded-sm">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5" />
                <div>
                  <p className="text-sm text-amber-400 font-medium">Dikkat!</p>
                  <p className="text-xs text-amber-400/80 mt-1">
                    Bu işlem eski ödeme uyarılarını temizleyecek ve müşteri bakiyelerini Excel'deki değerlere göre güncelleyecektir.
                    Toplam beklenen alacak: ₺4.055.166,74
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-3">
              <Button 
                onClick={handleSyncCustomerReceivables}
                disabled={syncingData}
                className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950"
                data-testid="sync-receivables-btn"
              >
                {syncingData ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Senkronize Ediliyor...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Bakiyeleri Senkronize Et
                  </>
                )}
              </Button>
              
              <Button 
                onClick={handleImportExcelTransactions}
                disabled={importingTransactions}
                className="bg-amber-400 hover:bg-amber-300 text-zinc-950"
                data-testid="import-transactions-btn"
              >
                {importingTransactions ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    İşlemler İçe Aktarılıyor...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    Excel İşlemlerini İçe Aktar
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* System Info */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-sky-400/10 rounded-sm">
            <SettingsIcon className="w-6 h-6 text-sky-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-outfit font-medium text-zinc-50">Sistem Bilgisi</h3>
            <p className="text-sm text-zinc-500 mt-1">
              Uygulama sürümü ve teknik detaylar
            </p>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="p-3 bg-zinc-950 border border-zinc-800 rounded-sm">
                <p className="text-xs text-zinc-500">Uygulama</p>
                <p className="text-sm text-zinc-50">Ajans Finans & İK Takip</p>
              </div>
              <div className="p-3 bg-zinc-950 border border-zinc-800 rounded-sm">
                <p className="text-xs text-zinc-500">Sürüm</p>
                <p className="text-sm text-zinc-50">1.1.0</p>
              </div>
              <div className="p-3 bg-zinc-950 border border-zinc-800 rounded-sm">
                <p className="text-xs text-zinc-500">Backend</p>
                <p className="text-sm text-zinc-50">FastAPI + MongoDB</p>
              </div>
              <div className="p-3 bg-zinc-950 border border-zinc-800 rounded-sm">
                <p className="text-xs text-zinc-500">Frontend</p>
                <p className="text-sm text-zinc-50">React + Tailwind</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Info */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6">
        <h3 className="text-lg font-outfit font-medium text-zinc-50 mb-4">Özellikler</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            "Müşteri yönetimi",
            "Ödeme ve tahsilat takibi",
            "Personel yönetimi",
            "İzin takibi",
            "Avans yönetimi",
            "Maaş hesaplama",
            "Puantaj takibi",
            "Vergi takibi",
            "Sigorta ödemeleri",
            "Dashboard ve raporlar",
            "Google Sheets senkronizasyonu",
            "Google Drive yedekleme",
            "AI belge analizi",
            "Hatırlatma sistemi"
          ].map((feature, index) => (
            <div key={index} className="flex items-center gap-2 text-sm text-zinc-400">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              {feature}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
